import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AzTagComponent } from '../az-tag/az-tag.component';

type ModalState =
  | 'confirm-change'
  | 'review'
  | 'confirm-reject'
  | 'success'
  | 'rejected'
  | 'already-processed'
  | 'request-sent'
  | 'processing';

@Component({
  selector: 'app-status-change-modal',
  standalone: true,
  imports: [CommonModule, AzTagComponent],
  templateUrl: './status-change-modal.component.html',
  styleUrl: './status-change-modal.component.scss',
})
export class StatusChangeModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() legalEntityName = '';
  @Input() currentStatus = '';
  @Input() newStatus = '';
  @Input() alreadyProcessedBy = '';
  @Input() initialState: ModalState = 'confirm-change'; // Default to confirm-change, can be overridden

  @Output() approve = new EventEmitter<void>();
  @Output() reject = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() confirmChange = new EventEmitter<void>();

  // Internal state management
  state: ModalState = 'confirm-change';

  ngOnChanges(changes: SimpleChanges) {
    // Use the initialState input when modal opens
    if (this.isOpen && changes['isOpen']) {
      this.state = this.initialState;
    }

    // If alreadyProcessedBy changes and has a value, show already-processed state
    if (changes['alreadyProcessedBy'] && this.alreadyProcessedBy && this.isOpen) {
      this.state = 'already-processed';
    }
  }

  // State: confirm-change -> User clicks "Request Change"
  onRequestChange() {
    // Show processing state while API call is in progress
    this.state = 'processing';
    // Emit after state change to allow UI to update
    setTimeout(() => this.confirmChange.emit(), 0);
  }

  // State: confirm-change -> User clicks "Cancel"
  onCancelChange() {
    this.onClose();
  }

  // State: review -> User clicks "Approve"
  onApprove() {
    // Show processing state while API call is in progress
    this.state = 'processing';
    // Emit after state change to allow UI to update
    setTimeout(() => this.approve.emit(), 0);
  }

  // State: review -> User clicks "Reject" -> show confirmation
  onReject() {
    this.state = 'confirm-reject';
  }

  // State: confirm-reject -> User clicks "Yes" (confirm rejection)
  onConfirmReject() {
    // Show processing state while API call is in progress
    this.state = 'processing';
    // Emit after state change to allow UI to update
    setTimeout(() => this.reject.emit(), 0);
  }

  // State: confirm-reject -> User clicks "No" (cancel rejection)
  onCancelReject() {
    this.state = 'review';
  }

  onClose() {
    this.state = 'review'; // Reset state on close
    this.close.emit();
  }

  // Show already processed state (called from parent when API returns error)
  showAlreadyProcessed() {
    this.state = 'already-processed';
  }

  // Show success state (called from parent after successful approval)
  showSuccess() {
    this.state = 'success';
    // Auto-close after 2 seconds
    setTimeout(() => {
      this.onClose();
    }, 2000);
  }

  // Show rejected state (called from parent after successful rejection)
  showRejected() {
    this.state = 'rejected';
    // Auto-close after 2 seconds
    setTimeout(() => {
      this.onClose();
    }, 2000);
  }

  // Show request-sent state (called from parent after successful request submission)
  showRequestSent() {
    this.state = 'request-sent';
    // Auto-close after 2 seconds
    setTimeout(() => {
      this.onClose();
    }, 2000);
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  // Helper methods to split status
  getCurrentStatusPrimary(): string {
    return this.getStatusPrimary(this.currentStatus);
  }

  getCurrentStatusSecondary(): string {
    return this.getStatusSecondary(this.currentStatus);
  }

  getNewStatusPrimary(): string {
    return this.getStatusPrimary(this.newStatus);
  }

  getNewStatusSecondary(): string {
    return this.getStatusSecondary(this.newStatus);
  }

  private getStatusPrimary(status: string): string {
    if (!status) return '';
    const idx = status.indexOf(':');
    if (idx === -1) return status.trim();
    return status.slice(0, idx).trim();
  }

  private getStatusSecondary(status: string): string {
    if (!status) return '';
    const idx = status.indexOf(':');
    if (idx === -1) return '';
    return status.slice(idx + 1).trim();
  }
}
